import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { X, ChevronUp, Loader2, ChevronLeft, ChevronRight, Users, MapPin, Layers, ExternalLink, BookOpen, Shuffle, Info } from 'lucide-react';
import { getCatalogueEpisode } from '../services/cms';
import { StatBadge } from '../components/StatBadge';

const AuthorAvatar = ({ author, size = 44 }) => (
  <div className="rounded-full overflow-hidden bg-primary/15 flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
    {author.portrait?.url
      ? <img src={author.portrait.url} alt={author.portrait.alt || `Portrait de ${author.name}`} loading="lazy" className="w-full h-full object-cover" />
      : <Users className="w-1/2 h-1/2 text-primary" />}
  </div>
);

const ToonReader = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const collection = params.get('collection') || '';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [scrollPct, setScrollPct] = useState(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    setData(null); setError(false); window.scrollTo(0, 0);
    getCatalogueEpisode(slug, collection).then(setData).catch(() => setError(true));
  }, [slug, collection]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setScrollPct(h > 0 ? Math.round((window.scrollY / h) * 100) : 0);
      setShowTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!data?.images) return;
    data.images.slice(2, 6).forEach((img) => { const p = new Image(); p.src = img.image_url; });
  }, [data]);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#141414] text-white">
      <p className="font-display text-xl">Épisode introuvable.</p>
      <Link to="/catalogue" className="text-primary font-800">Retour au catalogue</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#141414]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const { episode, images, prev, next, collection: ctx, collection_mismatch, recommendation } = data;
  const activeColl = ctx?.slug || '';
  const collQS = activeColl ? `?collection=${encodeURIComponent(activeColl)}` : '';
  const toonLink = (s) => `/toon/${encodeURIComponent(s)}${collQS}`;

  return (
    <div className="bg-[#141414] min-h-screen" data-testid="toon-reader">
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-black/40 z-50"><div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${scrollPct}%` }} /></div>
      <div className="fixed top-1.5 left-0 right-0 z-40 bg-[#141414]/85 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(activeColl ? `/collection/${encodeURIComponent(activeColl)}` : '/catalogue')} data-testid="reader-back-btn" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/90 text-white font-800 text-sm hover:bg-primary shrink-0 focus-visible:ring-4 focus-visible:ring-primary/40"><X className="w-4 h-4" /> <span className="hidden sm:inline">{activeColl ? 'Retour à la collection' : 'Retour au catalogue'}</span></button>
          <div className="min-w-0 text-center">
            <p className="font-display font-600 text-white text-sm truncate">{episode.titre}</p>
            {ctx && <p className="text-[11px] text-secondary font-800 truncate">{ctx.name} · {ctx.position}/{ctx.total}</p>}
          </div>
          <Link to="/catalogue" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 shrink-0"><BookOpen className="w-4 h-4 text-white" /></Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto pt-16">
        {collection_mismatch && (
          <div className="mx-4 mt-3 mb-1 flex items-start gap-2 rounded-xl bg-amber-500/15 border border-amber-500/30 px-3 py-2 text-amber-200 text-xs font-700" data-testid="reader-collection-mismatch">
            <Info className="w-4 h-4 shrink-0 mt-0.5" /> La collection demandée n’est pas associée à ce webtoon. Lecture normale ci-dessous.
          </div>
        )}
        {images.length === 0 ? (
          <div className="py-24 px-6 text-center" data-testid="reader-empty">
            <p className="text-white font-700 text-lg mb-5">Ce webtoon ne peut pas être chargé pour le moment.</p>
            <button onClick={() => navigate(activeColl ? `/collection/${encodeURIComponent(activeColl)}` : '/catalogue')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform"><BookOpen className="w-4 h-4" /> {activeColl ? 'Retour à la collection' : 'Retour au catalogue'}</button>
          </div>
        ) : (
          <div className="flex flex-col">
            {images.map((img, i) => (
              <img key={`${episode.source_id}-${img.order}`} src={img.image_url} alt={img.texte_alternatif || `planche ${i + 1}`}
                width={img.largeur || undefined} height={img.hauteur || undefined}
                loading={i < 2 ? 'eager' : 'lazy'} decoding="async"
                className="w-full h-auto block align-top"
                style={{ aspectRatio: img.largeur && img.hauteur ? `${img.largeur} / ${img.hauteur}` : undefined }} />
            ))}
          </div>
        )}

        {/* meta + navigation */}
        <div className="px-4 py-10">
          <div className="bg-white/5 rounded-2xl p-5 mb-8 text-white/80">
            <h3 className="font-display font-700 text-xl text-white mb-4">{episode.titre}</h3>
            <StatBadge stats={episode.stats} size="lg" className="text-white/70 mb-4" />
            {(episode.auteurs || []).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-800 uppercase text-white/50 mb-2.5 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Auteurs et autrices</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="reader-authors">
                  {episode.auteurs.map((a) => (
                    <Link key={a.slug} to={`/auteur/${encodeURIComponent(a.slug)}`} className="flex items-center gap-3 rounded-xl bg-white/5 hover:bg-primary/20 p-2 transition-colors">
                      <AuthorAvatar author={a} />
                      <span className="font-700 text-white text-sm truncate">{a.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {(episode.collections || []).length > 0 && (
              <div className="mb-3"><p className="text-xs font-800 uppercase text-white/50 mb-1.5 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Collection</p>
                <div className="flex flex-wrap gap-2">{episode.collections.map((c) => <Link key={c.slug} to={`/collection/${encodeURIComponent(c.slug)}`} className="px-3 py-1 rounded-full bg-primary/30 hover:bg-primary text-white text-sm font-700 transition-colors">{c.name}</Link>)}</div></div>
            )}
            {(episode.locations || []).length > 0 && (
              <div className="mb-3"><p className="text-xs font-800 uppercase text-white/50 mb-1.5 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Lieux</p>
                <div className="flex flex-wrap gap-2">{episode.locations.map((l) => <span key={l.slug} className="px-3 py-1 rounded-full bg-white/10 text-white text-sm font-700">{l.name}</span>)}</div></div>
            )}
            {episode.source_url && <a href={episode.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-secondary font-800 text-sm mt-1 hover:underline"><ExternalLink className="w-4 h-4" /> Voir sur citytoon.org</a>}
          </div>

          <div className="text-center">
            <div className="inline-block w-16 h-1 rounded bg-primary mb-6" />
            <h3 className="font-display font-700 text-2xl text-white mb-6">Fin de l’épisode</h3>

            {ctx && (
              <div className="mb-5">
                <Link to={`/collection/${encodeURIComponent(activeColl)}`} data-testid="reader-see-collection" className="inline-flex items-center gap-2 text-secondary font-800 hover:underline"><Layers className="w-4 h-4" /> Voir toute la collection « {ctx.name} »</Link>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch">
              {prev ? <Link to={toonLink(prev.slug)} data-testid="reader-prev-btn" className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-800 hover:bg-white/20 transition-colors"><ChevronLeft className="w-4 h-4" /> Précédent</Link> : null}
              <Link to={activeColl ? `/collection/${encodeURIComponent(activeColl)}` : '/catalogue'} className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 text-white font-800 hover:bg-white/20 transition-colors"><BookOpen className="w-4 h-4" /> {activeColl ? 'Collection' : 'Catalogue'}</Link>
              {next
                ? <Link to={toonLink(next.slug)} data-testid="reader-next-btn" className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform">Suivant <ChevronRight className="w-4 h-4" /></Link>
                : recommendation
                  ? <Link to={`/toon/${encodeURIComponent(recommendation.slug)}${recommendation.collection_slug ? `?collection=${encodeURIComponent(recommendation.collection_slug)}` : ''}`} data-testid="reader-discover-btn" className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-800 hover:scale-105 transition-transform"><Shuffle className="w-4 h-4" /> Découvrir un autre webtoon</Link>
                  : null}
            </div>
          </div>
        </div>
      </div>

      {showTop && <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-xl hover:scale-110 transition-transform"><ChevronUp className="w-6 h-6" /></button>}
    </div>
  );
};

export default ToonReader;
